import type { FolderDto } from "./folder.types.ts";
import type { LibraryGame } from "./adapter.ts";

/**
 * Library ordering model — ported from platform-2.0 (the in-game app) so
 * both libraries behave identically.
 * ----------------------
 * Folders and games living in the same container (the root, or one folder)
 * form ONE mixed list — a game can sit between two folders. Both kinds sort
 * by the SAME `position` scale: after every drag the whole library is
 * renumbered top-to-bottom into a single global sequence (folders take the
 * number where their subtree starts). That makes the report game pickers
 * trivially follow the library: they just sort games by `position`.
 *
 * The current (incomplete) game is special: always pinned to the very top of
 * its container, never draggable, never renumbered.
 */

export const isCurrentGame = (g: LibraryGame): boolean => g.complete === false;

/** Position-tied games keep the historical most-recent-first order. */
export const compareLibraryGames = (a: LibraryGame, b: LibraryGame): number => {
  const dp = (a.position ?? 0) - (b.position ?? 0);
  if (dp !== 0) return dp;
  const ad = a.game_date ?? "";
  const bd = b.game_date ?? "";
  if (ad < bd) return 1;
  if (ad > bd) return -1;
  return (b.game_id ?? 0) - (a.game_id ?? 0);
};

export type LibraryItem =
  | { kind: "folder"; folder: FolderDto }
  | { kind: "game"; game: LibraryGame };

export const libraryItemId = (item: LibraryItem): string =>
  item.kind === "folder"
    ? `folder-${item.folder.folder_id}`
    : `game-${item.game.game_id}`;

const positionOf = (item: LibraryItem): number =>
  item.kind === "folder" ? item.folder.position : (item.game.position ?? 0);

/**
 * Mixed-list comparator: shared position scale first; a position tie (the
 * whole library before its first-ever drag) keeps the legacy rendering —
 * folders before games, folders by id, games by date desc.
 */
export const compareLibraryItems = (a: LibraryItem, b: LibraryItem): number => {
  const dp = positionOf(a) - positionOf(b);
  if (dp !== 0) return dp;
  if (a.kind !== b.kind) return a.kind === "folder" ? -1 : 1;
  if (a.kind === "folder" && b.kind === "folder") {
    return a.folder.folder_id - b.folder.folder_id;
  }
  return compareLibraryGames(
    (a as { game: LibraryGame }).game,
    (b as { game: LibraryGame }).game,
  );
};

/**
 * The mixed, display-ordered item list of one container. The current game is
 * NOT included — callers pin it above this list.
 */
export function buildContainerItems(
  folders: FolderDto[],
  games: LibraryGame[],
  containerId: number | null,
): LibraryItem[] {
  const items: LibraryItem[] = [];
  for (const folder of folders) {
    if ((folder.parent_id ?? null) === containerId) {
      items.push({ kind: "folder", folder });
    }
  }
  for (const game of games) {
    if (game.game_id == null) continue;
    if (isCurrentGame(game)) continue;
    if ((game.folder_id ?? null) === containerId) {
      items.push({ kind: "game", game });
    }
  }
  items.sort(compareLibraryItems);
  return items;
}

/**
 * Flattened top-to-bottom game order of the whole library, mirroring exactly
 * what the tree renders: each container's mixed list in order, a folder
 * contributing its subtree at the spot it occupies. The current game always
 * comes first.
 */
export function flattenLibraryGameIds(
  folders: FolderDto[],
  games: LibraryGame[],
): number[] {
  const out: number[] = [];
  for (const g of games) {
    if (isCurrentGame(g) && g.game_id != null) out.push(g.game_id);
  }
  const visit = (containerId: number | null) => {
    for (const item of buildContainerItems(folders, games, containerId)) {
      if (item.kind === "game") {
        if (item.game.game_id != null) out.push(item.game.game_id);
      } else {
        visit(item.folder.folder_id);
      }
    }
  };
  visit(null);
  return out;
}

export type ReorderedLibraryPositions = {
  gameItems: Array<{ game_id: number; position: number }>;
  folderItems: Array<{ folder_id: number; position: number }>;
};

/**
 * After a drag drops `orderedItemIds` ("folder-N" / "game-N") as the new
 * mixed order of `containerId`, renumber the WHOLE library onto one global
 * sequence and return every game and folder whose stored position changes.
 * The current game is never renumbered (it's pinned, not ordered); the
 * sequence starts at 1 so nothing collides with its default 0.
 */
export function computeReorderedLibraryPositions(
  folders: FolderDto[],
  games: LibraryGame[],
  containerId: number | null,
  orderedItemIds: string[],
): ReorderedLibraryPositions {
  const orderIndex = new Map(orderedItemIds.map((id, i) => [id, i]));

  const itemsFor = (cid: number | null): LibraryItem[] => {
    const items = buildContainerItems(folders, games, cid);
    if (cid !== containerId) return items;
    // The dropped container follows the dragged order; anything the drag
    // didn't know about keeps its old relative spot at the end.
    return [...items].sort((a, b) => {
      const ai = orderIndex.get(libraryItemId(a));
      const bi = orderIndex.get(libraryItemId(b));
      if (ai != null && bi != null) return ai - bi;
      if (ai != null) return -1;
      if (bi != null) return 1;
      return compareLibraryItems(a, b);
    });
  };

  const gameItems: Array<{ game_id: number; position: number }> = [];
  const folderItems: Array<{ folder_id: number; position: number }> = [];
  let counter = 1;
  const visit = (cid: number | null) => {
    for (const item of itemsFor(cid)) {
      if (item.kind === "game") {
        const position = counter++;
        if (
          item.game.game_id != null &&
          (item.game.position ?? 0) !== position
        ) {
          gameItems.push({ game_id: item.game.game_id, position });
        }
      } else {
        const position = counter++;
        if (item.folder.position !== position) {
          folderItems.push({ folder_id: item.folder.folder_id, position });
        }
        visit(item.folder.folder_id);
      }
    }
  };
  visit(null);
  return { gameItems, folderItems };
}
